import { useState } from 'react';
import { capitalizeFirst } from '../../shared/sentence';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { reloadCompanyModules, saveCompanyModule } from '../companyModulesApi';
import { modulesOf, type CompanyModuleState } from '../modules';
import { useHasPermission } from '../PermissionGate';

/**
 * Moduły spółki (T.01 „instancja minimalna”, 06.10.2026): „zachować dobór
 * modułów dla firmy”. Przełącznik przy każdym module, zmiana z powodem
 * (trafia do dziennika działań) — jak blokada Trade i giełd obok.
 *
 * Wyłączony moduł znika z menu i kart, a API odpowiada na jego trasach 403
 * — samo ukrycie nie wyłączałoby backendu. Domyślnie wszystko jest włączone.
 * Zmiana wymaga zarządzania spółkami (administrator instancji); bez niego
 * przełączniki są tylko do odczytu.
 */
export const CompanyModules = ({
  record,
  onSaved,
}: {
  record: Record<string, unknown>;
  onSaved?: () => void;
}) => {
  const canManage = useHasPermission('MANAGE_COMPANIES');
  const [editing, setEditing] = useState<CompanyModuleState | null>(null);
  const modules = modulesOf(record);
  if (modules.length === 0) return <span>—</span>;

  return (
    <Stack spacing={0.25}>
      {modules.map((m) => {
        const byDependency = m.ownEnabled && !m.enabled;
        const tip = byDependency
          ? `${m.description} Nie działa, bo wyłączony jest moduł, od którego zależy.`
          : m.reason
            ? `${m.description} Ostatnia zmiana: ${m.reason}${m.changedBy ? ` (${m.changedBy.username})` : ''}.`
            : m.description;
        return (
          // `describeChild` — opis idzie do aria-describedby; nazwą przełącznika
          // zostaje nazwa modułu, nie cały opis.
          <Tooltip key={m.key} title={tip} placement="left" describeChild>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  size="small"
                  checked={m.ownEnabled}
                  disabled={!canManage}
                  onChange={() => setEditing(m)}
                />
              }
              label={
                <Typography
                  variant="body2"
                  color={m.enabled ? 'text.primary' : 'text.secondary'}
                >
                  {m.label}
                  {byDependency ? ' (nie działa — zależność wyłączona)' : ''}
                </Typography>
              }
            />
          </Tooltip>
        );
      })}
      {editing && (
        <CompanyModuleDialog
          record={record}
          module={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reloadCompanyModules();
            onSaved?.();
          }}
        />
      )}
    </Stack>
  );
};

function CompanyModuleDialog({
  record,
  module,
  onClose,
  onSaved,
}: {
  record: Record<string, unknown>;
  module: CompanyModuleState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const target = !module.ownEnabled;
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await saveCompanyModule(record.id as number, module.key, target, reason.trim());
      onSaved();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ? capitalizeFirst(msg) : 'Nie udało się zapisać');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {target ? 'Włączyć' : 'Wyłączyć'} moduł „{module.label}” — {String(record.name ?? '')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {target
            ? `${module.description} Moduł wróci do menu i na karty, a jego funkcje zaczną działać.`
            : `${module.description} Po wyłączeniu ekrany modułu znikną, a system odmówi jego operacji dla tej spółki. Dane zostają.`}
        </Typography>
        <TextField
          label="Powód"
          required
          fullWidth
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error && (
          <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button onClick={save} variant="contained" disabled={busy || !reason.trim()}>
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
}
